import type { GraphModification, ConceptAttribute, Graph } from "@lr-notitia/notitia-engineer/schemas";
import type { Terminology } from "@lr-notitia/notitia-dictionary/schemas";
import { Ref, computed } from "vue";
import { api } from "@lr-notitia/notitia-engineer/client";
import { handleGraphModification } from "./utils";
import * as Metrics from "../metrics";
import { useSymbolsStore } from "../symbols";

let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	symbolsStore = useSymbolsStore();
}

export const conceptAttributes = (graph: Ref<Graph>) =>
	computed<ConceptAttribute[]>(() => {
		if (!symbolsStore) init();
		return (
			Object.values(graph.value.nodes).filter(
				(node) => node.type === "attribute" && node.layer === "concept"
			) as ConceptAttribute[]
		).sort((a, b) =>
			symbolsStore
				.term({ id: a.refersTo, terminology: a.terminology || {} })
				.toLocaleLowerCase()
				.localeCompare(
					symbolsStore.term({ id: b.refersTo, terminology: b.terminology || {} }).toLocaleLowerCase()
				)
		);
	});
export function conceptAttribute(domainId: Ref<string>, graph: Ref<Graph>) {
	return {
		byId: (id: string) => {
			Metrics.recordAccess(id);
			return graph.value.nodes[id] as ConceptAttribute | undefined;
		},
		add: (data: { refersTo: string; localTerminology: Terminology }) => add(domainId, graph, data),
		update: (attribute: ConceptAttribute) => update(domainId, graph, attribute),
		remove: (id: string) => remove(domainId, graph, id),
		order: (update: GraphModification) => order(domainId, graph, update),
	};
}

function add(
	domainId: Ref<string>,
	graph: Ref<Graph>,
	data: { refersTo: string; localTerminology: Terminology }
): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		api()
			.domain(domainId.value)
			.attributes()
			.post(data)
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function update(domainId: Ref<string>, graph: Ref<Graph>, attribute: ConceptAttribute): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[attribute.id]) return reject();
		api()
			.domain(domainId.value)
			.attribute(attribute.id)
			.put(attribute)
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function remove(domainId: Ref<string>, graph: Ref<Graph>, id: string): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[id]) return reject();
		api()
			.domain(domainId.value)
			.attribute(id)
			.del()
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}

function order(domainId: Ref<string>, graph: Ref<Graph>, update: GraphModification): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		api()
			.domain(domainId.value)
			.attributes()
			.order()
			.patch(update)
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
